import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconToolsKitchen2, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useBranches } from '../branches/branchesApi';
import { useDeleteKitchenStation, useKitchenStations } from './stationsApi';
import { StationFormDrawer } from './StationFormDrawer';
import type { KitchenStationDto } from './types';

export function StationsPage() {
  const { t, i18n } = useTranslation();
  const branchesQuery = useBranches();
  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data]);

  // Default to the first branch when one lands — multi-branch tenants
  // pick another with the Select. Stations are scoped per-branch on the
  // server so the page never lists across branches.
  const [branchId, setBranchId] = useState<string | null>(null);
  const effectiveBranchId = branchId ?? branches[0]?.branchId ?? null;

  const stationsQuery = useKitchenStations(effectiveBranchId);
  const deleteMutation = useDeleteKitchenStation(effectiveBranchId);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<KitchenStationDto | null>(null);
  const [deleting, setDeleting] = useState<KitchenStationDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (s: KitchenStationDto) => {
    setEditing(s);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.kitchenStationId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const stations = stationsQuery.data ?? [];
  const hasStations = stations.length > 0;

  const branchOptions = branches.map((b) => ({
    value: b.branchId,
    label: i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr),
  }));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('kitchenStations.page.title')}
        subtitle={t('kitchenStations.page.subtitle')}
        actions={
          <Button
            leftSection={<IconPlus size={16} stroke={1.8} />}
            onClick={openCreate}
            disabled={!effectiveBranchId}
          >
            {t('kitchenStations.page.create')}
          </Button>
        }
      />

      <Paper p="md">
        <Select
          label={t('kitchenStations.page.branchLabel')}
          data={branchOptions}
          value={effectiveBranchId}
          onChange={setBranchId}
          searchable
          disabled={branchesQuery.isLoading || branches.length === 0}
          maw={360}
        />
      </Paper>

      <Paper p={hasStations ? 0 : 'lg'}>
        {stationsQuery.isLoading || branchesQuery.isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !effectiveBranchId ? (
          <EmptyState
            icon={<IconToolsKitchen2 size={22} stroke={1.6} />}
            title={t('kitchenStations.empty.noBranchesTitle')}
            description={t('kitchenStations.empty.noBranchesBody')}
          />
        ) : !hasStations ? (
          <EmptyState
            icon={<IconToolsKitchen2 size={22} stroke={1.6} />}
            title={t('kitchenStations.empty.title')}
            description={t('kitchenStations.empty.body')}
            actionLabel={t('kitchenStations.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('kitchenStations.columns.code')}</Table.Th>
                <Table.Th>{t('kitchenStations.columns.nameAr')}</Table.Th>
                <Table.Th>{t('kitchenStations.columns.nameEn')}</Table.Th>
                <Table.Th>{t('kitchenStations.columns.sortOrder')}</Table.Th>
                <Table.Th>{t('kitchenStations.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {stations.map((s) => (
                <Table.Tr key={s.kitchenStationId}>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {s.code}
                    </span>
                  </Table.Td>
                  <Table.Td fw={500}>
                    <Group gap="xs" wrap="nowrap">
                      <span>{s.nameAr}</span>
                      {s.isDefault && (
                        <Badge color="blue" variant="light" radius="sm" size="sm">
                          {t('kitchenStations.defaultBadge')}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{s.nameEn ?? '—'}</Table.Td>
                  <Table.Td className="font-numeric">{s.sortOrder}</Table.Td>
                  <Table.Td>
                    {s.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(s)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={
                          s.isDefault
                            ? t('kitchenStations.defaultLockedTooltip')
                            : t('common.delete')
                        }
                        withinPortal
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(s)}
                          aria-label={t('common.delete')}
                          disabled={s.isDefault}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {effectiveBranchId && (
        <StationFormDrawer
          opened={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          branchId={effectiveBranchId}
          station={editing}
        />
      )}

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('kitchenStations.delete.title')}
        message={t('kitchenStations.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
